'use client';

import { useState, useRef, useCallback } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { FileOutput, Upload, RotateCcw, ArrowRight, Download, FileText, AlertCircle, Eye } from 'lucide-react';
import Link from 'next/link';
import { ToolSignupWall } from './tool-signup-wall';

interface UblToPdfConverterProps {
  locale: Locale;
  content: ToolContent;
}

interface ParsedInvoice {
  invoiceNumber: string;
  issueDate: string;
  dueDate?: string;
  currency: string;
  sellerName: string;
  sellerAddress?: string;
  sellerVat?: string;
  buyerName: string;
  buyerAddress?: string;
  lines: { description: string; quantity: number; price: number; total: number }[];
  subtotal: number;
  vatAmount: number;
  total: number;
}

function parseUblXml(xml: string): ParsedInvoice | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');

    if (doc.querySelector('parsererror')) return null;

    const getText = (tagName: string, parent?: Element): string => {
      const el = (parent || doc).getElementsByTagNameNS('*', tagName)[0];
      return el?.textContent?.trim() || '';
    };

    const invoiceNumber = getText('ID');
    const issueDate = getText('IssueDate');
    const dueDate = getText('DueDate') || undefined;
    const currency = getText('DocumentCurrencyCode') || 'EUR';

    // Seller
    const supplierParty = doc.getElementsByTagNameNS('*', 'AccountingSupplierParty')[0];
    const sellerName = supplierParty ? getText('Name', supplierParty) : '';
    const sellerStreet = supplierParty ? getText('StreetName', supplierParty) : '';
    const sellerCity = supplierParty ? getText('CityName', supplierParty) : '';
    const sellerPostal = supplierParty ? getText('PostalZone', supplierParty) : '';
    const sellerAddress = [sellerStreet, sellerPostal, sellerCity].filter(Boolean).join(', ');
    const sellerVat = supplierParty ? getText('CompanyID', supplierParty) : '';

    // Buyer
    const customerParty = doc.getElementsByTagNameNS('*', 'AccountingCustomerParty')[0];
    const buyerName = customerParty ? getText('Name', customerParty) : '';
    const buyerStreet = customerParty ? getText('StreetName', customerParty) : '';
    const buyerCity = customerParty ? getText('CityName', customerParty) : '';
    const buyerPostal = customerParty ? getText('PostalZone', customerParty) : '';
    const buyerAddress = [buyerStreet, buyerPostal, buyerCity].filter(Boolean).join(', ');

    // Lines
    const lines: ParsedInvoice['lines'] = [];
    const invoiceLines = doc.getElementsByTagNameNS('*', 'InvoiceLine');
    const creditNoteLines = doc.getElementsByTagNameNS('*', 'CreditNoteLine');
    const allLines = invoiceLines.length > 0 ? invoiceLines : creditNoteLines;

    for (let i = 0; i < allLines.length; i++) {
      const line = allLines[i];
      const description = getText('Name', line) || getText('Description', line) || `Item ${i + 1}`;
      const quantity = parseFloat(getText('InvoicedQuantity', line) || getText('CreditedQuantity', line) || '1');
      const price = parseFloat(getText('PriceAmount', line) || '0');
      const total = parseFloat(getText('LineExtensionAmount', line) || '0');
      lines.push({ description, quantity, price, total });
    }

    // Totals
    const subtotal = parseFloat(getText('TaxExclusiveAmount') || '0');
    const vatAmount = parseFloat(doc.getElementsByTagNameNS('*', 'TaxAmount')[0]?.textContent || '0');
    const total = parseFloat(getText('PayableAmount') || getText('TaxInclusiveAmount') || '0');

    return {
      invoiceNumber,
      issueDate,
      dueDate,
      currency,
      sellerName,
      sellerAddress,
      sellerVat,
      buyerName,
      buyerAddress,
      lines,
      subtotal,
      vatAmount,
      total,
    };
  } catch {
    return null;
  }
}

export function UblToPdfConverter({ locale, content }: UblToPdfConverterProps) {
  const [xmlContent, setXmlContent] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [parsedInvoice, setParsedInvoice] = useState<ParsedInvoice | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const labels = content.labels;
  const errors = content.errors ?? {};

  const processXml = useCallback((xml: string) => {
    setIsProcessing(true);
    setError(null);

    setTimeout(() => {
      const parsed = parseUblXml(xml);
      if (parsed) {
        setParsedInvoice(parsed);
      } else {
        setError(errors.notUbl ?? 'This is not a valid UBL document');
      }
      setIsProcessing(false);
    }, 500);
  }, [errors.notUbl]);

  const handleFileUpload = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xml')) {
      setError(errors.invalidXml ?? 'Invalid XML file');
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setXmlContent(text);
      processXml(text);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleTextChange = (text: string) => {
    setXmlContent(text);
    setFileName('');
    if (text.trim().length > 50) {
      processXml(text);
    } else {
      setParsedInvoice(null);
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const downloadPdf = () => {
    if (!parsedInvoice) return;

    // Generate a simple HTML invoice and trigger print/save as PDF
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${parsedInvoice.invoiceNumber}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
    .invoice-title { font-size: 24px; font-weight: bold; color: #1e293b; }
    .invoice-number { color: #64748b; }
    .parties { display: flex; justify-content: space-between; margin-bottom: 40px; }
    .party { width: 45%; }
    .party-label { font-size: 12px; color: #64748b; margin-bottom: 8px; }
    .party-name { font-weight: bold; margin-bottom: 4px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 12px; color: #64748b; }
    td { padding: 12px; border-bottom: 1px solid #e2e8f0; }
    .totals { text-align: right; }
    .totals-row { display: flex; justify-content: flex-end; margin-bottom: 8px; }
    .totals-label { width: 150px; color: #64748b; }
    .totals-value { width: 100px; font-weight: bold; }
    .grand-total { font-size: 18px; border-top: 2px solid #1e293b; padding-top: 12px; margin-top: 12px; }
    .watermark { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; display: flex; align-items: center; justify-content: space-between; }
    .watermark-left { display: flex; align-items: center; }
    .disclaimer { margin-top: 12px; padding: 12px 16px; background: #fef9ee; border: 1px solid #fde68a; border-radius: 6px; font-size: 11px; color: #92400e; line-height: 1.5; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="invoice-title">INVOICE</div>
      <div class="invoice-number">${parsedInvoice.invoiceNumber}</div>
    </div>
    <div style="text-align: right">
      <div><strong>Date:</strong> ${parsedInvoice.issueDate}</div>
      ${parsedInvoice.dueDate ? `<div><strong>Due:</strong> ${parsedInvoice.dueDate}</div>` : ''}
    </div>
  </div>

  <div class="parties">
    <div class="party">
      <div class="party-label">FROM</div>
      <div class="party-name">${parsedInvoice.sellerName || 'Seller'}</div>
      <div>${parsedInvoice.sellerAddress || ''}</div>
      ${parsedInvoice.sellerVat ? `<div>VAT: ${parsedInvoice.sellerVat}</div>` : ''}
    </div>
    <div class="party">
      <div class="party-label">TO</div>
      <div class="party-name">${parsedInvoice.buyerName || 'Customer'}</div>
      <div>${parsedInvoice.buyerAddress || ''}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th style="text-align: right">Qty</th>
        <th style="text-align: right">Price</th>
        <th style="text-align: right">Total</th>
      </tr>
    </thead>
    <tbody>
      ${parsedInvoice.lines.map(line => `
        <tr>
          <td>${line.description}</td>
          <td style="text-align: right">${line.quantity}</td>
          <td style="text-align: right">${formatCurrency(line.price, parsedInvoice.currency)}</td>
          <td style="text-align: right">${formatCurrency(line.total, parsedInvoice.currency)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="totals">
    <div class="totals-row">
      <div class="totals-label">Subtotal:</div>
      <div class="totals-value">${formatCurrency(parsedInvoice.subtotal, parsedInvoice.currency)}</div>
    </div>
    <div class="totals-row">
      <div class="totals-label">VAT:</div>
      <div class="totals-value">${formatCurrency(parsedInvoice.vatAmount, parsedInvoice.currency)}</div>
    </div>
    <div class="totals-row grand-total">
      <div class="totals-label">Total:</div>
      <div class="totals-value">${formatCurrency(parsedInvoice.total, parsedInvoice.currency)}</div>
    </div>
  </div>

  <div class="disclaimer">This document was converted from UBL with the free tool by Winkel Factuur (winkelfactuur.nl). It is provided as-is and does not constitute legal or tax advice.</div>

  <div class="watermark">
    <div class="watermark-left">
      <img src="https://winkelfactuur.nl/bimi.svg" alt="" width="14" height="14" style="vertical-align: middle; margin-right: 6px;" />
      <span>Generated by Winkel Factuur – winkelfactuur.nl</span>
    </div>
    <span>Free tool</span>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const reset = () => {
    setXmlContent('');
    setFileName('');
    setParsedInvoice(null);
    setError(null);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100">
          <FileOutput className="h-7 w-7 text-blue-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {content.title}
        </h2>
        <p className="mt-3 text-lg text-slate-600">
          {content.description}
        </p>
      </div>

      {/* Converter Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* File Upload */}
        <div
          className={`relative mb-4 rounded-xl border-2 border-dashed p-8 text-center transition-all ${
            isDragging ? 'border-brand-500 bg-brand-50' : 'border-slate-300 hover:border-slate-400'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
          <Upload className="mx-auto h-10 w-10 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">{labels.dragDrop}</p>
          {fileName && (
            <p className="mt-2 flex items-center justify-center gap-2 text-sm text-brand-600">
              <FileText className="h-4 w-4" />
              {fileName}
            </p>
          )}
        </div>

        {/* Or Paste XML */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            {labels.orPasteXml}
          </label>
          <textarea
            value={xmlContent}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={labels.pasteXml}
            rows={6}
            className="block w-full rounded-xl border border-slate-300 p-4 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* Processing */}
        {isProcessing && (
          <div className="mb-4 rounded-xl bg-brand-50 p-4 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600"></div>
            <p className="mt-2 text-sm font-medium text-brand-700">{labels.processing}</p>
          </div>
        )}

        {/* Preview */}
        {parsedInvoice && (
          <div className="mb-4 rounded-xl bg-slate-50 p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Eye className="h-4 w-4" />
              {labels.preview}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Invoice #</span>
                <span className="font-medium">{parsedInvoice.invoiceNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Date</span>
                <span className="font-medium">{parsedInvoice.issueDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">From</span>
                <span className="font-medium">{parsedInvoice.sellerName || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">To</span>
                <span className="font-medium">{parsedInvoice.buyerName || '-'}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium text-slate-700">Total</span>
                <span className="font-semibold">{formatCurrency(parsedInvoice.total, parsedInvoice.currency)}</span>
              </div>
            </div>

            <p className="mt-3 text-xs text-slate-500">{labels.watermarkNotice}</p>

            <button
              type="button"
              onClick={downloadPdf}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-700"
            >
              <Download className="h-4 w-4" />
              {labels.download}
            </button>
          </div>
        )}

        {/* Reset Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.reset}
        </button>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA Banner */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Instructions */}
      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
                  {index + 1}
                </span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {content.disclaimer.heading}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            {content.disclaimer.content}
          </p>
        </div>
      )}
    </div>
  );
}
